<script>
    import { onMount, createEventDispatcher } from 'svelte';
    // Removed svelte-select dependency - using native inputs instead

    export let date = null; // Currently only accepts strings w/ format 'YYYY-MM-DD'
    export let minYear = 1930;
    export let maxYear = new Date().getFullYear() + 1;
    export let disabled = false;

    const dispatch = createEventDispatcher();

    let dayValue = '';
    let monthValue = '';
    let yearValue = '';

    let dayInput;
    let monthInput;
    let yearInput;

    const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    $: {
        if (dayValue && monthValue && yearValue) {
            // Validate and dispatch
            const day = parseInt(dayValue);
            const month = parseInt(monthValue);
            const year = parseInt(yearValue);

            if (isValidDate(day, month, year)) {
                const paddedDay = day <= 9 ? `0${day}` : `${day}`;
                const paddedMonth = month <= 9 ? `0${month}` : `${month}`;
                dispatch('change', {
                    date: `${year}-${paddedMonth}-${paddedDay}`,
                });
            }
        }
    }

    onMount(() => {
        if (date != null) {
            let splitDate = date.split('-');
            yearValue = splitDate[0];
            monthValue = parseInt(splitDate[1]).toString();
            dayValue = parseInt(splitDate[2]).toString();
            
            // Set the formatted values in the inputs
            if (dayInput) {
                const dayNum = parseInt(dayValue);
                dayInput.value = dayNum <= 9 ? `0${dayNum}` : `${dayNum}`;
            }
            if (monthInput) {
                const monthNum = parseInt(monthValue);
                monthInput.value = monthNum <= 9 ? `0${monthNum}` : `${monthNum}`;
            }
            if (yearInput) {
                yearInput.value = yearValue;
            }
        }
    });

    function isValidDate(day, month, year) {
        if (!day || !month || !year) return false;
        if (month < 1 || month > 12) return false;
        if (year < minYear || year > maxYear) return false;

        const maxDays = getMaxDays(month, year);
        if (day < 1 || day > maxDays) return false;

        return true;
    }

    function getMaxDays(month, year) {
        if (month === 2 && isLeapYear(year)) {
            return 29;
        }
        return DAYS[month - 1];
    }

    function isLeapYear(year) {
        return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    }

    function handleDayInput(e) {
        let value = e.target.value;
        
        // Allow only numbers
        let numericValue = value.replace(/\D/g, '');
        
        if (numericValue.length > 2) {
            numericValue = numericValue.slice(0, 2);
        }
        
        const num = parseInt(numericValue);
        if (num > 31) {
            numericValue = '31';
        }
        
        dayValue = numericValue;
        e.target.value = numericValue; // Set the actual input value to prevent letters
        
        // Auto-advance to month field
        if (numericValue.length === 2 && monthInput) {
            monthInput.focus();
        }
    }

    function handleMonthInput(e) {
        let value = e.target.value;
        
        // Allow only numbers
        let numericValue = value.replace(/\D/g, '');
        
        if (numericValue.length > 2) {
            numericValue = numericValue.slice(0, 2);
        }
        
        const num = parseInt(numericValue);
        if (num > 12) {
            numericValue = '12';
        }
        
        monthValue = numericValue;
        e.target.value = numericValue; // Set the actual input value to prevent letters
        
        // Auto-advance to year field
        if (numericValue.length === 2 && yearInput) {
            yearInput.focus();
        }
        
        // Adjust day if needed
        if (dayValue) {
            const maxDays = getMaxDays(parseInt(monthValue), parseInt(yearValue) || 2024);
            if (parseInt(dayValue) > maxDays) {
                dayValue = maxDays.toString();
            }
        }
    }

    function handleYearInput(e) {
        let value = e.target.value;
        
        // Allow only numbers
        let numericValue = value.replace(/\D/g, '');
        
        if (numericValue.length > 4) {
            numericValue = numericValue.slice(0, 4);
        }
        
        yearValue = numericValue;
        e.target.value = numericValue; // Set the actual input value to prevent letters
    }

    function handleDayBlur(e) {
        if (dayValue) {
            const num = parseInt(dayValue);
            if (num >= 1 && num <= 31) {
                // Format with leading zero on blur
                const formatted = num <= 9 ? `0${num}` : `${num}`;
                dayValue = num.toString(); // Keep internal value as number string
                e.target.value = formatted; // Display formatted value
            } else {
                dayValue = '';
                e.target.value = '';
            }
        }
    }

    function handleMonthBlur(e) {
        if (monthValue) {
            const num = parseInt(monthValue);
            if (num >= 1 && num <= 12) {
                // Format with leading zero on blur
                const formatted = num <= 9 ? `0${num}` : `${num}`;
                monthValue = num.toString(); // Keep internal value as number string
                e.target.value = formatted; // Display formatted value
            } else {
                monthValue = '';
                e.target.value = '';
            }
        }
    }

    function handleYearBlur(e) {
        if (yearValue) {
            const num = parseInt(yearValue);
            if (num >= minYear && num <= maxYear) {
                yearValue = num.toString();
                e.target.value = yearValue;
            } else {
                yearValue = '';
                e.target.value = '';
            }
        }
    }
    
    function handleDayFocus(e) {
        // Remove formatting when focused to show raw number
        if (dayValue) {
            e.target.value = dayValue;
        }
    }
    
    function handleMonthFocus(e) {
        // Remove formatting when focused to show raw number
        if (monthValue) {
            e.target.value = monthValue;
        }
    }
</script>

<div class="ds">
    <div class="ds__input-wrapper">
        <input
            bind:this={dayInput}
            type="text"
            inputmode="numeric"
            placeholder="DD"
            on:input={handleDayInput}
            on:focus={handleDayFocus}
            on:blur={handleDayBlur}
            {disabled}
            class="ds__input"
            maxlength="2"
        />
    </div>
    <div class="ds__input-wrapper">
        <input
            bind:this={monthInput}
            type="text"
            inputmode="numeric"
            placeholder="MM"
            on:input={handleMonthInput}
            on:focus={handleMonthFocus}
            on:blur={handleMonthBlur}
            {disabled}
            class="ds__input"
            maxlength="2"
        />
    </div>
    <div class="ds__input-wrapper">
        <input
            bind:this={yearInput}
            type="text"
            inputmode="numeric"
            placeholder="YYYY"
            on:input={handleYearInput}
            on:blur={handleYearBlur}
            {disabled}
            class="ds__input"
            maxlength="4"
        />
    </div>
</div>

<style lang="scss" type="text/scss">
    @use 'src/sass/base' as *;

    .ds {
        display: grid;
        justify-items: center;
        align-items: center;
        grid-template-columns: 1fr 1fr 1fr;
        column-gap: em(20);
        width: 100%;

        &__input-wrapper {
            width: 100%;
            position: relative;
        }

        &__input {
            width: 100%;
            padding: 15px 20px;
            font-size: em(14);
            font-weight: $fw-regular;
            border: 1px solid #c7c7ca;
            border-radius: 8px;
            background: white;
            transition: all 0.2s ease;
            text-align: center;

            &:focus {
                outline: none;
                border-color: #662cc8;
                box-shadow: 0 0 0 1px #662cc8;
            }

            &:disabled {
                background: #fbfbfb;
                color: #000;
                cursor: not-allowed;
            }

            &::placeholder {
                color: #c7c7ca;
            }
        }
    }
</style>
